import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Warning, Clock, Circle, ArrowsClockwise } from '@phosphor-icons/react';
import { fetchCalendarSyncHealth, type ConnectionHealth } from '@/lib/calendarSyncApi';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const HEALTH_STYLES: Record<ConnectionHealth, { label: string; cls: string }> = {
  healthy: { label: 'Healthy', cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  stale:   { label: 'Stale',   cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' },
  error:   { label: 'Error',   cls: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300' },
  never:   { label: 'Never synced', cls: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' },
};

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card px-4 py-3">
      <div className={`text-xl font-bold ${tone ?? 'text-foreground'}`}>{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

export default function CalendarSyncHealthPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['calendar-sync-health'],
    queryFn: fetchCalendarSyncHealth,
    refetchInterval: 60_000,
  });

  return (
    <div className="mx-auto max-w-4xl pb-20">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Calendar Sync Health</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Operational view of every connected calendar — for support and diagnostics.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
        >
          <ArrowsClockwise size={12} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted/40" />
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Could not load health data.</p>
      ) : (
        <>
          {/* Worker heartbeat banner */}
          <div className={`mb-5 flex items-center justify-between rounded-xl border px-4 py-3 ${
            data.summary.pollWorkerHealthy ? 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10' : 'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10'
          }`}>
            <div className="flex items-center gap-2 text-sm">
              {data.summary.pollWorkerHealthy
                ? <CheckCircle size={16} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
                : <Warning size={16} weight="fill" className="text-red-600 dark:text-red-400" />}
              <span className="font-semibold text-foreground">
                Poll worker {data.summary.pollWorkerHealthy ? 'running' : 'NOT running'}
              </span>
              <span className="text-muted-foreground">— last ran {fmt(data.summary.lastPollAt)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Failed jobs: <span className="font-semibold text-foreground">{data.summary.failedJobs ?? 'n/a (Redis?)'}</span>
            </div>
          </div>

          {/* Summary stats */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Active" value={data.summary.activeConnections} />
            <Stat label="Healthy" value={data.summary.healthy} tone="text-emerald-600 dark:text-emerald-400" />
            <Stat label="Stale" value={data.summary.stale} tone={data.summary.stale ? 'text-amber-600 dark:text-amber-400' : undefined} />
            <Stat label="Error" value={data.summary.error} tone={data.summary.error ? 'text-red-600 dark:text-red-400' : undefined} />
            <Stat label="Never" value={data.summary.never} />
          </div>

          {/* Connection table */}
          <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Account</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Last sync</th>
                  <th className="px-4 py-2.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.connections.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No calendar connections yet.</td></tr>
                ) : data.connections.map((c) => {
                  const s = HEALTH_STYLES[c.health];
                  return (
                    <tr key={c.userId} className="border-b border-border/30 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{c.emailAddress}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>
                          {c.health === 'healthy' && <CheckCircle size={9} weight="bold" />}
                          {c.health === 'stale' && <Clock size={9} weight="bold" />}
                          {c.health === 'error' && <Warning size={9} weight="bold" />}
                          {c.health === 'never' && <Circle size={9} weight="bold" />}
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {fmt(c.lastSyncAt)}
                        {c.minutesSinceSync !== null && <span className="ml-1 text-[11px]">({c.minutesSinceSync}m ago)</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {c.lastSyncError
                          ? <span className="text-red-600 dark:text-red-400">{c.lastSyncError}</span>
                          : <span className="text-muted-foreground/60">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
